import { Grid, InputAdornment, Typography } from "@mui/material";
import { postAdd, updatePost } from "Apis/post";
import MDBox from "components/MDBox";
import TinyTextEditor from "components/MDTinyTextEditor";
import { LoginFormContext } from "context/LoginFormContext";
import { ModalContext } from "context/ModalContext";
import { useFormik } from "formik";
import React, { useContext, useState, useEffect } from "react";
import * as Yup from "yup";
import PropTypes from "prop-types";
import MDSelectBox from "components/MDSelectBox";
import SubmitButton from "components/SubmitButton";
import MDInput from "components/MDInput";
import { EventBookingContext } from "context/EventBookingContext";
import { addEventBookingApi } from "Apis/eventBooking";
import { MemberContext } from "context/MemberContext";
import { getSubMemberListApi } from "Apis/admin";

const EventBookingForm = ({ eventBookingData }) => {
  const [event, setEvent] = useState([]);
  const [member, setMember] = useState(null);
  const [subMember, setSubMember] = useState([]);
  const [selectedSubMember, setSelectedSubMember] = useState(null);
  const [showMember, setShowMember] = useState(false);
  const [showSubMember, setShowSubMember] = useState(false);
  const { setNotification } = useContext(LoginFormContext);
  const { editData, upcomingEventData, getUpcomingEventList } = useContext(EventBookingContext);
  const { parentMemberList, parentDataListing } = useContext(MemberContext);
  const { setOpen, formIsEdit } = useContext(ModalContext);

  const initialValues = {
    member_id: null,
    event_id: null,
    total_amount: 0,
    total_event_amount: 0,
    no_of_bookings: "",
    no_of_guests: "",
    members: [],
  };

  // Dynamic validation schema based on event type
  const getValidationSchema = () => {
    return Yup.object().shape({
      event_id: Yup.string().required("Event is required!"),
      total_amount: Yup.string().required("Total amount is required!"),
      total_event_amount: Yup.string().required("Total event amount is required!"),
      // Conditional validation for member_id (required only if member_only === 1)
      member_id: Yup.string().when([], {
        is: () => event?.member_only === 1,
        then: (schema) => schema.required("Member is required!"),
        otherwise: (schema) => schema.nullable(),
      }),
      // Conditional validation for no_of_bookings (required only if member_only === 0)
      no_of_bookings: Yup.string().when([], {
        is: () => event?.member_only === 0,
        then: (schema) => schema.required("Number of bookings is required!"),
        otherwise: (schema) => schema.nullable(),
      }),

      members: Yup.array()
        .nullable()
        .when([], {
          is: () => event?.member_only === 1,
          then: (schema) => {
            if (event?.is_allow_guest_booking === 1) {
              return schema.test(
                "members-or-guests",
                "Either members or number of guests is required!",
                function (value) {
                  const { no_of_guests } = this.parent;
                  return (value && value.length > 0) || (!!no_of_guests && no_of_guests > 0);
                }
              );
            } else {
              return schema.test(
                "members-required",
                "Please select at least one member!",
                function (value) {
                  return value && value.length > 0;
                }
              );
            }
          },
        }),

      // Guests field
      no_of_guests: Yup.number()
        .nullable()
        .when([], {
          is: () => event?.member_only === 1 && event?.is_allow_guest_booking === 1,
          then: (schema) =>
            schema.test(
              "members-or-guests",
              "Either members or number of guests is required!",
              function (value) {
                const { members } = this.parent;
                return (members && members.length > 0) || (!!value && value > 0);
              }
            ),
          otherwise: (schema) => schema.nullable(),
        }),
    });
  };

  const calculateTotalEventAmount = (baseAmount, memberCount, guestCount, guestAmount) => {
    const totalGuestAmount = Number(guestAmount || 0) * Number(guestCount || 0);
    return Number(baseAmount || 0) * Number(memberCount || 0) + totalGuestAmount;
  };

  const getEventabel = (id) => {
    const event = upcomingEventData.find((item) => item.value === id);
    return event ? event.label : "-";
  };

  const getMemberLabel = (id) => {
    const member = parentMemberList.find((item) => item.value === id);
    return member ? member.label : "-";
  };

  const getSubMemberList = async (memberId) => {
    try {
      const response = await getSubMemberListApi({
        parent_id: memberId,
        event_id: event?.value,
      });
      const subMemberData = response?.data?.data?.map((data) => ({
        label: `${data?.first_name} ${data?.middle_name} ${data?.surname}`,
        value: data?.id,
        imageUrl: data?.member_images[0]?.image,
      }));
      setSubMember(subMemberData || []);
      setShowSubMember(true);
    } catch (error) {
      console.error("Error fetching submembers:", error);
      setSubMember([]);
    }
  };

  useEffect(() => {
    if (formIsEdit) {
      setEvent(
        editData.event_id
          ? { label: getEventabel(editData.event_id), value: editData.event_id }
          : null
      );
      setMember(
        editData.member_id
          ? { label: getMemberLabel(editData.member_id), value: editData.member_id }
          : null
      );
    }
    getUpcomingEventList();
    parentDataListing();
  }, []);

  const handleSubmit = async (values) => {
    values = {
      ...values,
      no_of_bookings: values?.members?.length ? values?.members?.length : values?.no_of_bookings,
      no_of_guests: event?.is_allow_guest_booking === 1 ? values?.no_of_guests : null,
    };

    if (!formIsEdit) {
      let response = await addEventBookingApi(values);
      if (response?.status === 200) {
        eventBookingData();
        setOpen(false);
        setNotification({
          color: "success",
          icon: "check",
          title: "Success",
          content: response.data.message,
          open: true,
        });
      } else {
        setNotification({
          color: "error",
          icon: "cancel",
          title: "Error",
          content: response.data.message,
          open: true,
        });
      }
    } else {
      // update logic can go here
    }
  };

  const handleEventValue = (value) => {
    if (value) {
      formik.setFieldValue("event_id", value.value);
      formik.setFieldValue("total_event_amount", value?.total_amount || 0);
      formik.setFieldValue("total_amount", value?.total_amount || 0);
      setShowMember(true);
      formik.setFieldValue("member_id", null);
      formik.setFieldValue("members", []);
      setMember(null);
      setSelectedSubMember([]);
      setEvent(value);
    } else {
      formik.setFieldValue("event_id", null);
      formik.setFieldValue("total_amount", 0);
      formik.setFieldValue("member_id", null);
      formik.setFieldValue("members", []);
      formik.setFieldValue("no_of_bookings", "");
      formik.setFieldValue("no_of_guests", null);
      formik.setFieldValue("total_event_amount", 0);
      setShowMember(false);
      setShowSubMember(false);
      setEvent(null);
      setMember(null);
      setSelectedSubMember([]);
    }
  };

  const handleMember = async (data) => {
    if (data) {
      formik.setFieldValue("member_id", data?.value);
      setMember(data);

      // Reset submember
      formik.setFieldValue("members", []);
      setSelectedSubMember([]);
      await getSubMemberList(data?.value);
    } else {
      formik.setFieldValue("member_id", null);
      formik.setFieldValue("members", []);
      setMember(null);
      setSelectedSubMember([]);
      setSubMember([]);
      setShowSubMember(false);
    }
  };

  const handleSubMember = (data) => {
    if (data) {
      const selectedValues = data?.map((item) => item?.value);
      formik.setFieldValue("members", selectedValues);
      setSelectedSubMember(data);
    } else {
      formik.setFieldValue("members", []);
      setSelectedSubMember([]);
    }
  };

  const formik = useFormik({
    initialValues: formIsEdit ? editData : initialValues,
    validationSchema: getValidationSchema(),
    onSubmit: handleSubmit,
    enableReinitialize: true, // Important: Re-initialize when validation schema changes
  });

  // Auto-calculate total_event_amount based on event type
  useEffect(() => {
    let memberCount = 0;
    let guestCount = 0;

    if (event?.member_only === 1) {
      // For member-only events, use members array length
      memberCount = formik.values.members?.length || 0;
      guestCount = formik.values.no_of_guests || 0;
    } else if (event?.member_only === 0) {
      // For non-member events, use no_of_bookings
      memberCount = Number(formik.values.no_of_bookings) || 0;
    }

    const total = calculateTotalEventAmount(
      formik.values.total_amount,
      memberCount,
      guestCount,
      event?.guest_price
    );

    // Only update if the calculated value is different to avoid infinite loops
    if (formik.values.total_event_amount !== total) {
      formik.setFieldValue("total_event_amount", total);
    }
  }, [
    formik.values.total_amount,
    formik.values.members,
    formik.values.no_of_bookings,
    event?.member_only,
    formik.values.no_of_guests,
  ]);

  return (
    <MDBox>
      <Grid item xs={12} md={12} component="form" role="form" onSubmit={formik.handleSubmit}>
        <Grid container spacing={2} rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
          {/* Event Selection */}
          <Grid item xs={12} md={6}>
            <MDBox sx={{ width: "100%", paddingTop: "20px !important" }}>
              <MDSelectBox
                name="event_id"
                label="Select Event"
                multiple={false}
                defaultValue={event}
                options={upcomingEventData}
                handleChange={(value) => handleEventValue(value)}
                onBlur={formik.handleBlur}
                width="100%"
                required
              />
              {formik.touched.event_id && formik.errors.event_id && (
                <MDBox
                  component="p"
                  sx={{ fontSize: "12px", marginLeft: "5px", color: "red", marginTop: "5px" }}
                >
                  {formik.errors.event_id}
                </MDBox>
              )}
            </MDBox>
          </Grid>

          {/* Member Selection */}
          <Grid item xs={12} md={6}>
            <MDBox sx={{ width: "100%", paddingTop: "20px !important" }}>
              <MDSelectBox
                name="member_id"
                defaultValue={member}
                options={parentMemberList}
                handleChange={(value) => handleMember(value)}
                label="Head Member"
                width="100%"
                isImage={true}
                multiple={false}
                onBlur={formik.handleBlur}
                required={event?.member_only === 1}
                disabled={!showMember}
              />
              {formik.touched.member_id && formik.errors.member_id && (
                <MDBox
                  component="p"
                  sx={{ fontSize: "12px", marginLeft: "5px", color: "red", marginTop: "5px" }}
                >
                  {formik.errors.member_id}
                </MDBox>
              )}
            </MDBox>
          </Grid>

          {/* Total Amount */}
          <Grid item xs={12} md={6}>
            <MDBox sx={{ marginTop: "10px" }}>
              <MDInput
                type="number"
                name="total_amount"
                label="Event Amount"
                variant="standard"
                value={formik.values.total_amount}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                required
                InputProps={{
                  readOnly: true,
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
              />
              {formik.touched.total_amount && formik.errors.total_amount && (
                <MDBox
                  component="p"
                  sx={{ fontSize: "12px", marginLeft: "5px", color: "red", marginTop: "5px" }}
                >
                  {formik.errors.total_amount}
                </MDBox>
              )}
            </MDBox>
          </Grid>

          {/* Total Event Amount */}
          <Grid item xs={12} md={6}>
            <MDBox sx={{ marginTop: "10px" }}>
              <MDInput
                type="number"
                name="total_event_amount"
                label="Total Event Amount"
                variant="standard"
                value={formik.values.total_event_amount}
                fullWidth
                required
                InputProps={{
                  readOnly: true,
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
              />
              {formik.touched.total_event_amount && formik.errors.total_event_amount && (
                <MDBox
                  component="p"
                  sx={{ fontSize: "12px", marginLeft: "5px", color: "red", marginTop: "5px" }}
                >
                  {formik.errors.total_event_amount}
                </MDBox>
              )}
            </MDBox>
          </Grid>

          {/* Number of Bookings - Only for non-member events */}
          {event?.member_only === 0 && (
            <Grid item xs={12} md={6}>
              <MDBox sx={{ marginTop: "10px" }}>
                <MDInput
                  type="text"
                  name="no_of_bookings"
                  label="Total Members"
                  variant="standard"
                  value={formik.values.no_of_bookings}
                  onChange={(e) => {
                    const value = e.target.value === "" ? 0 : Number(e.target.value);
                    if (/^\d*$/.test(value)) {
                      formik.setFieldValue("no_of_bookings", value);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  fullWidth
                  required
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                />
                {formik.touched.no_of_bookings && formik.errors.no_of_bookings && (
                  <MDBox
                    component="p"
                    sx={{ fontSize: "12px", marginLeft: "5px", color: "red", marginTop: "5px" }}
                  >
                    {formik.errors.no_of_bookings}
                  </MDBox>
                )}
              </MDBox>
            </Grid>
          )}

          {/* Submember Selection */}
          {showSubMember && subMember.length > 0 && event?.member_only === 1 && member && (
            <Grid item xs={12} md={12}>
              <MDBox sx={{ width: "100%", paddingTop: "20px !important" }}>
                <MDSelectBox
                  name="members"
                  defaultValue={selectedSubMember}
                  options={subMember}
                  handleChange={(value) => handleSubMember(value)}
                  label="Members"
                  width="100%"
                  isImage={true}
                  multiple={true}
                  onBlur={formik.handleBlur}
                  required={event?.member_only === 1 && event?.is_allow_guest_booking === 0}
                />
                {formik.touched.members && formik.errors.members && !formik.errors.no_of_guests && (
                  <MDBox
                    component="p"
                    sx={{ fontSize: "12px", marginLeft: "5px", color: "red", marginTop: "5px" }}
                  >
                    {formik.errors.members}
                  </MDBox>
                )}
              </MDBox>
            </Grid>
          )}
          {subMember.length === 0 && event?.member_only === 1 && member && (
            <Grid mt={1} item>
              <Typography variant="caption" fontWeight="bold">
                {member
                  ? `${member.first_name ?? ""} ${member.last_name ?? ""} and their family members have successfully booked for this event.`
                  : "The selected head member has no available child members for booking."}
              </Typography>
            </Grid>
          )}
          {/* Number of Member Guests Bookings - Only for member guest allow events */}
          {event?.member_only === 1 && event?.is_allow_guest_booking === 1 && (
            <Grid item xs={12} md={6}>
              <MDBox sx={{ marginTop: "10px" }}>
                <MDInput
                  type="number"
                  name="no_of_guests"
                  label="Total Member Guests"
                  variant="standard"
                  value={formik.values.no_of_guests}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  fullWidth
                />
                {formik.touched.no_of_guests && formik.errors.no_of_guests && (
                  <MDBox
                    component="p"
                    sx={{ fontSize: "12px", marginLeft: "5px", color: "red", marginTop: "5px" }}
                  >
                    {formik.errors.no_of_guests}
                  </MDBox>
                )}
              </MDBox>
            </Grid>
          )}

          {/* Submit */}
          <MDBox sx={{ width: "100%", textAlign: "center", marginTop: "20px" }}>
            <SubmitButton btnText={formIsEdit ? "Update" : "Add"} type="submit" />
          </MDBox>
        </Grid>
      </Grid>
    </MDBox>
  );
};

EventBookingForm.propTypes = {
  eventBookingData: PropTypes.func,
};

export default EventBookingForm;
